import type { SorRecord } from '@circuitry-ai/doc-ui';
import {
  DEFAULT_ROUTING_OPTIONS,
  type WorkQueueDraft,
} from './work-queue-editor-utils';
import type {
  WorkQueue,
  WorkQueueActionRecordData,
  WorkQueueAssignmentMethod,
  WorkQueueAssignmentPolicy,
  WorkQueueEntityExpandedDefinition,
  WorkQueueEntityRecordData,
  WorkQueueEntityType,
  WorkQueueEscalationPolicy,
  WorkQueueRecordData,
  WorkQueueRoutingMethod,
} from './work-queues-types';

/** Fields of a SOR record the mappers need; matches both query items and create/update responses */
export type WorkQueueRecordLike = Pick<
  SorRecord<WorkQueueRecordData>,
  'id' | 'display' | 'natural_key' | 'revision' | 'data'
>;

/** queue_entities query item with its definition expanded */
export type WorkQueueEntityRecord = SorRecord<WorkQueueEntityRecordData> & {
  expanded?: { definition?: WorkQueueEntityExpandedDefinition };
};

/** SOR `assignmentMethod` -> UI routing method */
const ASSIGNMENT_METHOD_TO_ROUTING: Record<
  WorkQueueAssignmentMethod,
  WorkQueueRoutingMethod
> = {
  MANUAL: 'manual',
  PULL: 'shared_pull',
  ROUND_ROBIN: 'round_robin',
  LEAST_LOAD: 'load_balanced',
  // Closest UI card; the original value is kept on save unless the user picks another method
  SKILL_BASED: 'rules_based',
};

/** UI routing method -> SOR `assignmentMethod`. Methods missing here have no API equivalent yet. */
const ROUTING_TO_ASSIGNMENT_METHOD: Partial<
  Record<WorkQueueRoutingMethod, WorkQueueAssignmentMethod>
> = {
  manual: 'MANUAL',
  shared_pull: 'PULL',
  round_robin: 'ROUND_ROBIN',
  load_balanced: 'LEAST_LOAD',
};

const KEY_PATTERN = /^[a-z][a-z0-9_]{1,62}$/;
const MAX_KEY_LENGTH = 63;

/** Turns free text into a SOR key matching ^[a-z][a-z0-9_]{1,62}$ */
/** Strips leading and trailing underscores (a loop instead of a backtracking-prone regex) */
function trimUnderscores(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && value[start] === '_') start++;
  while (end > start && value[end - 1] === '_') end--;
  return value.slice(start, end);
}

export function toSorKey(value: string, fallback = 'work_queue'): string {
  // Non-alphanumerics -> "_", then drop leading non-letters (keys must start with a letter)
  const normalized = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '');
  const key = trimUnderscores(normalized.slice(0, MAX_KEY_LENGTH));
  return KEY_PATTERN.test(key) ? key : fallback;
}

/** Unique queueKey for a new queue; appends _2, _3... when the base key is taken */
function createUniqueQueueKey(name: string, existingKeys: string[]): string {
  return createUniqueSorKey(name, existingKeys);
}

/** Unique SOR key for `name`; appends _2, _3... when the base key is taken */
export function createUniqueSorKey(
  name: string,
  existingKeys: Iterable<string>,
  fallback = 'work_queue'
): string {
  const base = toSorKey(name, fallback);
  const taken = new Set(existingKeys);
  if (!taken.has(base)) {
    return base;
  }
  for (let index = 2; ; index++) {
    const suffix = `_${index}`;
    const candidate = `${base.slice(
      0,
      MAX_KEY_LENGTH - suffix.length
    )}${suffix}`;
    if (!taken.has(candidate)) {
      return candidate;
    }
  }
}

const minutesToHours = (minutes: number | null | undefined): number | null =>
  typeof minutes === 'number' && minutes > 0
    ? Math.round((minutes / 60) * 100) / 100
    : null;

const hoursToMinutes = (hours: number | null | undefined): number | null =>
  typeof hours === 'number' && hours > 0
    ? Math.max(1, Math.round(hours * 60))
    : null;

/** Policy the UI edits: highest-precedence enabled policy (lowest precedence number wins) */
function getPrimaryPolicy(
  policies: WorkQueueAssignmentPolicy[] | undefined
): WorkQueueAssignmentPolicy | undefined {
  const list = policies ?? [];
  const enabled = list.filter((policy) => policy.enabled !== false);
  return [...(enabled.length ? enabled : list)].sort(
    (a, b) => (a.precedence ?? 100) - (b.precedence ?? 100)
  )[0];
}

/** Effective method: the primary policy's override, else the queue-level assignmentMethod */
function getEffectiveAssignmentMethod(
  data: WorkQueueRecordData,
  policy: WorkQueueAssignmentPolicy | undefined
): WorkQueueAssignmentMethod | undefined {
  const policyMethod = policy?.assignmentMethod?.toUpperCase();
  return (
    policyMethod && policyMethod !== 'INHERIT'
      ? policyMethod
      : data.assignmentMethod?.toUpperCase()
  ) as WorkQueueAssignmentMethod | undefined;
}

const ESCALATION_KEY = 'not_started_escalation';
const REMINDER_KEY = 'assignee_reminder';
/** Used when creating the reminder entry; existing entries keep their stored timing */
const DEFAULT_REMINDER_AFTER_MINUTES = 60;
const DEFAULT_REMINDER_REPEAT_MINUTES = 60;
/** "Notify the assignee" on: in-app (PAL) and email */
const DEFAULT_NOTIFICATION_METHODS = ['IN_APP', 'EMAIL'];

/** "Escalate if not started after" entry: lowest-level NOT_STARTED escalation to a role */
function findRoleEscalation(
  policies: WorkQueueEscalationPolicy[]
): WorkQueueEscalationPolicy | undefined {
  return policies
    .filter(
      (policy) =>
        policy.trigger?.when === 'NOT_STARTED' && policy.target?.type === 'ROLE'
    )
    .sort((a, b) => a.level - b.level)[0];
}

/** "Send inspection reminders" entry: NOTIFY the assignee */
function findAssigneeReminder(
  policies: WorkQueueEscalationPolicy[]
): WorkQueueEscalationPolicy | undefined {
  return policies.find(
    (policy) => policy.target?.type === 'ASSIGNEE' && policy.action === 'NOTIFY'
  );
}

type MapWorkQueueOptions = {
  /** Enabled state of the queue's CLAIM queue_action record (they're stored separately) */
  allowSelfAssign?: boolean;
};

export function mapWorkQueueRecord(
  record: WorkQueueRecordLike,
  { allowSelfAssign = false }: MapWorkQueueOptions = {}
): WorkQueue {
  const data = record.data ?? {};
  const policy = getPrimaryPolicy(data.assignmentPolicies);
  const target = policy?.assignmentTarget;
  const assignmentMethod = getEffectiveAssignmentMethod(data, policy);
  const teamRole =
    (target?.type === 'ROLE' ? target.roleKey : target?.userId) ?? '';
  const escalation = findRoleEscalation(data.escalationPolicies ?? []);
  const reminder = findAssigneeReminder(data.escalationPolicies ?? []);

  return {
    queue_id: record.id,
    name:
      data.name ??
      record.display ??
      data.queueKey ??
      record.natural_key ??
      record.id,
    description: data.description ?? '',
    // Matches WorkQueueEntityType.uuid
    entity_type: policy?.sourceDefinitionId ?? '',
    team_role: teamRole,
    // Members live in the queue_member definition and aren't expanded on the queue record
    members: [],
    routing_method:
      (assignmentMethod && ASSIGNMENT_METHOD_TO_ROUTING[assignmentMethod]) ??
      'shared_pull',
    routing_options: {
      ...DEFAULT_ROUTING_OPTIONS,
      rules: [],
      territories: {},
      assigner_role: teamRole,
      unassigned_alert_hours: minutesToHours(data.responseSlaMinutes),
      allow_self_assign: allowSelfAssign,
      notify_assignee: (data.assigneeNotificationMethods ?? []).length > 0,
    },
    sla_hours: minutesToHours(data.completionSlaMinutes) ?? 0,
    escalation_hours: minutesToHours(escalation?.trigger.afterMinutes),
    escalation_role: escalation?.target.roleId ?? '',
    send_reminders: !!reminder && reminder.enabled !== false,
    is_active: (data.operatingStatus ?? 'ACTIVE').toUpperCase() === 'ACTIVE',
    source_record: { id: record.id, revision: record.revision, data },
  };
}

/** On keeps the stored channels (or defaults to in-app + email); off stores [] (don't notify) */
function buildNotificationMethods(
  notify: boolean,
  original: string[] | undefined
): string[] {
  if (!notify) {
    return [];
  }
  return original?.length ? original : DEFAULT_NOTIFICATION_METHODS;
}

/**
 * Applies the Service levels fields to escalationPolicies[], leaving entries the form doesn't edit:
 * - escalation hours + role -> NOT_STARTED entry targeting the role (removed when either is cleared)
 * - send reminders          -> the ASSIGNEE NOTIFY entry's enabled flag (created when first turned on)
 */
function buildEscalationPolicies(
  draft: WorkQueueDraft,
  original: WorkQueueEscalationPolicy[]
): WorkQueueEscalationPolicy[] {
  const escalation = findRoleEscalation(original);
  const reminder = findAssigneeReminder(original);
  const policies = original.filter(
    (policy) => policy !== escalation && policy !== reminder
  );

  const afterMinutes = hoursToMinutes(draft.escalation_hours);
  if (afterMinutes && draft.escalation_role) {
    policies.push({
      ...escalation,
      escalationKey: escalation?.escalationKey ?? ESCALATION_KEY,
      level: escalation?.level ?? 1,
      enabled: true,
      trigger: { when: 'NOT_STARTED', afterMinutes },
      target: { type: 'ROLE', roleId: draft.escalation_role },
      action: escalation?.action ?? 'NOTIFY',
    });
  }

  if (reminder || draft.send_reminders) {
    policies.push({
      escalationKey: REMINDER_KEY,
      level: 1,
      trigger: {
        when: 'NOT_COMPLETED',
        afterMinutes: DEFAULT_REMINDER_AFTER_MINUTES,
      },
      target: { type: 'ASSIGNEE' },
      action: 'NOTIFY',
      repeatEveryMinutes: DEFAULT_REMINDER_REPEAT_MINUTES,
      ...reminder,
      enabled: draft.send_reminders,
    });
  }
  return policies;
}

const CLAIM_ACTION_KEY = 'claim';

/** "Let members assign items to themselves"; PULL queues are self-assigned by definition */
export function isClaimActionEnabled(draft: WorkQueueDraft): boolean {
  return (
    draft.routing_method === 'shared_pull' ||
    (draft.routing_method === 'manual' &&
      draft.routing_options.allow_self_assign)
  );
}

/** The queue's CLAIM action record, if any */
export function findClaimAction<T extends { data: WorkQueueActionRecordData }>(
  actions: T[]
): T | undefined {
  return actions.find(
    (action) =>
      action.data?.kind === 'QUEUE_OPERATION' &&
      action.data.queueOperation === 'CLAIM'
  );
}

/** `data` for a new CLAIM queue_action record (one per queue, switched with `enabled`) */
export function buildClaimActionData(
  queueId: string,
  enabled: boolean
): WorkQueueActionRecordData {
  return {
    queueId,
    actionKey: CLAIM_ACTION_KEY,
    label: 'Claim',
    kind: 'QUEUE_OPERATION',
    queueOperation: 'CLAIM',
    requiredQueueRole: 'AGENT',
    allowedItemStates: ['READY'],
    // Applies to every entity type the queue routes
    sourceDefinitionId: null,
    requireComment: false,
    enabled,
  };
}

type BuildQueueDataOptions = {
  /** Queue being edited; omit for create */
  existing?: WorkQueue;
  /** Entity type (definition) selected in the form */
  entityType?: WorkQueueEntityType;
  /** queueKeys already in use, for generating a unique key on create */
  existingQueueKeys: string[];
};

/**
 * Builds the `data` payload for POST/PUT /api/v1/records (core.workqueue.queue).
 * On edit it starts from the stored data so fields the form doesn't show (capacityLimit,
 * fallbackQueueId, extra policies, ...) are preserved by the full-replace PUT.
 */
export function buildWorkQueueRecordData(
  draft: WorkQueueDraft,
  { existing, entityType, existingQueueKeys }: BuildQueueDataOptions
): WorkQueueRecordData {
  const original = existing?.source_record?.data ?? {};
  const routingMethod = draft.routing_method as WorkQueueRoutingMethod;
  const originalMethod = getEffectiveAssignmentMethod(
    original,
    getPrimaryPolicy(original.assignmentPolicies)
  );
  // Keep a method the UI can't express (e.g. SKILL_BASED) if the user didn't change the routing card
  const keepOriginalMethod =
    !!existing &&
    !!originalMethod &&
    ASSIGNMENT_METHOD_TO_ROUTING[originalMethod] === routingMethod;
  const assignmentMethod = keepOriginalMethod
    ? original.assignmentMethod?.toUpperCase() ?? 'PULL'
    : ROUTING_TO_ASSIGNMENT_METHOD[routingMethod] ?? 'PULL';

  const originalStatus = original.operatingStatus?.toUpperCase();
  const wasActive = (originalStatus ?? 'ACTIVE') === 'ACTIVE';
  let operatingStatus = draft.is_active ? 'ACTIVE' : 'PAUSED';
  // DRAINING/CLOSED have no toggle state; keep the stored status unless the user flips the switch
  if (existing && originalStatus && draft.is_active === wasActive) {
    operatingStatus = originalStatus;
  }

  const queueKey =
    original.queueKey ?? createUniqueQueueKey(draft.name, existingQueueKeys);

  const policies = [...(original.assignmentPolicies ?? [])];
  const primary = getPrimaryPolicy(policies);
  const purpose =
    primary?.purpose ??
    toSorKey(entityType?.name ?? 'work_queue', 'work_queue');
  const primaryPolicy: WorkQueueAssignmentPolicy = {
    ...(primary ?? {
      policyKey: toSorKey(`default_${purpose}`, 'default_policy'),
      precedence: 100,
      assignmentMethod: 'INHERIT',
      condition: null,
    }),
    // A policy-level method override would win over the routing card the user just picked
    ...(keepOriginalMethod ? {} : { assignmentMethod: 'INHERIT' }),
    enabled: true,
    purpose,
    sourceDefinitionId: draft.entity_type,
    assignmentTarget: {
      type: 'ROLE',
      roleKey: draft.team_role,
      userId: null,
      userFilter: null,
    },
  };
  if (primary) {
    policies[policies.indexOf(primary)] = primaryPolicy;
  } else {
    policies.push(primaryPolicy);
  }

  const data: WorkQueueRecordData = {
    ...original,
    name: draft.name.trim(),
    queueKey,
    defaultPriority: original.defaultPriority ?? 100,
    operatingStatus,
    assignmentMethod,
    assignmentPolicies: policies,
    // "Alert if unassigned after" is only shown for Manual routing
    responseSlaMinutes:
      routingMethod === 'manual'
        ? hoursToMinutes(draft.routing_options.unassigned_alert_hours)
        : null,
    completionSlaMinutes: hoursToMinutes(draft.sla_hours),
    preventSelfApproval: original.preventSelfApproval ?? false,
    // Keep stored channels when notifications stay on
    assigneeNotificationMethods: buildNotificationMethods(
      draft.routing_options.notify_assignee,
      original.assigneeNotificationMethods
    ),
    escalationPolicies: buildEscalationPolicies(
      draft,
      original.escalationPolicies ?? []
    ),
    // The selected entity type keeps its stored switch; it's added as enabled when not listed
    entityTypes: original.entityTypes?.some(
      (entry) => entry.sourceDefinitionId === draft.entity_type
    )
      ? original.entityTypes
      : [
          ...(original.entityTypes ?? []),
          { sourceDefinitionId: draft.entity_type, enabled: true },
        ],
  };

  // description has minLength 1, so omit it instead of sending ""
  const description = draft.description.trim();
  if (description) {
    data.description = description;
  } else {
    delete data.description;
  }
  return data;
}

const ENTITY_TYPE_ICONS: [RegExp, string][] = [
  [/inspect/i, 'solar:clipboard-check-linear'],
  [/work.?order|repair|service/i, 'solar:sledgehammer-linear'],
  [/payment|invoice|payee/i, 'solar:card-linear'],
  [/claim/i, 'solar:shield-check-linear'],
  [/support|case|ticket/i, 'solar:headphones-round-linear'],
];

/** core.workqueue.queue_entities record (queried with expand: ['definition']) -> entity type card */
export function mapQueueEntityRecord(
  record: WorkQueueEntityRecord
): WorkQueueEntityType {
  const definition = record.expanded?.definition;
  const definitionId = record.data?.definitionId ?? definition?.id ?? '';
  const source = definition?.key ?? '';
  const name =
    definition?.display || record.data?.description || source || definitionId;
  const label = `${name} ${source}`;
  return {
    // sourceDefinitionId on the queue's assignment policy
    uuid: definitionId,
    key: source || definitionId,
    record_id: record.id,
    record_revision: record.revision,
    name,
    description: record.data?.description ?? '',
    icon:
      ENTITY_TYPE_ICONS.find(([pattern]) => pattern.test(label))?.[1] ??
      'solar:database-linear',
    source,
    is_enabled: record.data?.enabled !== false,
    routing_fields: [],
    statuses: [],
  };
}
